import React, { FC, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';

import { ICreateProfilePictureRequestDTO, IProfilePictureDTO, IUpdateProfilePictureRequestDTO } from 'src/api/dtos';
import { FileUploadButton } from 'src/shared/components/FileUploadButton';
import { CameraIcon, CheckCircleIcon, CloseFilledIcon, DeleteIcon } from 'src/shared/components/icons';
import { Modal, ZOOM_PAN_IMAGE_MAX_SCALE, ZOOM_PAN_IMAGE_MIN_SCALE, ZoomPanImage } from 'src/shared/components/index';
import { ModalFooterProps } from 'src/shared/components/Modal/ModalFooter';
import { ModalHeaderProps } from 'src/shared/components/Modal/ModalHeader';
import { DEFAULT_MAX_IMAGE_SIZE_IN_MB } from 'src/shared/constants';
import { ProfilePictureTypeEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';
import { buildFormData } from 'src/shared/utils';

interface IState {
  scale: number;
  position: { x: number; y: number };
  imageSrc: string | undefined;
  uploadedFile: File | undefined;
  errorMessage?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  initials: string | undefined;
  picture: IProfilePictureDTO | undefined | null;
  onDelete: () => void;
  onUpdate: (payload: IUpdateProfilePictureRequestDTO) => void;
  onUpload: (formData: FormData) => void;
  loading: boolean;
}

export const ProfilePictureModal: FC<Props> = ({
  open,
  onClose,
  initials,
  picture,
  onDelete,
  onUpdate,
  onUpload,
  loading,
}) => {
  const { t } = useTranslate();

  const initialState: IState = {
    scale: picture?.scale ?? 1,
    position: { x: picture?.positionX ?? 0, y: picture?.positionY ?? 0 },
    imageSrc: picture?.pictureUri,
    uploadedFile: undefined,
  };

  const [state, setState] = useState<IState>(initialState);

  useEffect(() => {
    setState(initialState);
  }, [picture]);

  const dirty =
    !isEqual(initialState, state) &&
    // use case when we have updated or created a new image
    (!!state.uploadedFile ||
      // use case when we have removed the image
      (!state.uploadedFile && !!picture)); // use case when

  const handleScaleChange = (scale: number) => {
    setState((prevState) => ({ ...prevState, scale }));
  };

  const handleSliderChange = (_: Event, scale: number | number[]) => {
    if (Array.isArray(scale)) return;
    handleScaleChange(scale);
  };

  const handlePositionChange = (position: IState['position']) => {
    setState((prevState) => ({ ...prevState, position }));
  };

  const handleClose = () => {
    setState(initialState);
    onClose();
  };

  const handleFileRemove = () => {
    setState((prevState) => ({ ...prevState, uploadedFile: undefined, imageSrc: undefined, errorMessage: undefined }));
  };

  const handleFileChange = (newFile: File) => {
    setState((prevState) => ({
      ...prevState,
      uploadedFile: newFile,
      imageSrc: URL.createObjectURL(newFile),
      errorMessage: undefined,
    }));
  };

  const handleSubmit = () => {
    const isDeleteAction = !!picture && !state.uploadedFile && !state.imageSrc;

    if (isDeleteAction) {
      // Delete picture
      onDelete();
      return;
    }

    const options = {
      scale: state.scale ? +state.scale.toFixed(3) : undefined,
      positionX: +state.position.x.toFixed(3),
      positionY: +state.position.y.toFixed(3),
    };

    const isUpdateAction =
      !!picture &&
      state.imageSrc === picture.pictureUri &&
      !isEqual(options, pick(picture, ['scale', 'positionX', 'positionY']));

    if (isUpdateAction) {
      // Update picture options
      const payload: IUpdateProfilePictureRequestDTO = { ...options };
      onUpdate({ fileName: state.uploadedFile?.name ?? picture.fileName, ...payload });
      return;
    }

    if (state.uploadedFile) {
      // Upsert picture
      const payload: ICreateProfilePictureRequestDTO = {
        fileName: state.uploadedFile.name,
        type: ProfilePictureTypeEnums.ProfilePicture,
        ...options,
      };

      const formData = buildFormData<ICreateProfilePictureRequestDTO>({
        file: state.uploadedFile,
        otherParams: payload,
      });

      onUpload(formData);
    }
  };

  const modalHeaderProps: ModalHeaderProps = {
    title: t('files.uploadImage'),
    onClose: handleClose,
  };

  const modalFooterProps: ModalFooterProps = {
    submitText: t('global.save'),
    submitIcon: <CheckCircleIcon />,
    onSubmit: handleSubmit,
    closeText: t('global.cancel'),
    closeIcon: <CloseFilledIcon />,
    loading,
    disabled: !dirty,
    onClose: handleClose,
  };

  return (
    <Modal open={open} onClose={handleClose} modalHeaderProps={modalHeaderProps} modalFooterProps={modalFooterProps}>
      <Stack direction="column" alignItems="center" gap={2}>
        {state.errorMessage && <Alert severity="error">{state.errorMessage}</Alert>}

        <Stack
          justifyContent="center"
          alignItems="center"
          width={188}
          height={188}
          sx={{
            borderRadius: '50%',
            borderWidth: 4,
            borderStyle: 'solid',
            borderColor: (theme) => theme.palette.primary.main,
          }}
        >
          {state.imageSrc ? (
            <ZoomPanImage
              imageSrc={state.imageSrc}
              scale={state.scale}
              onScaleChange={handleScaleChange}
              position={state.position}
              onPositionChange={handlePositionChange}
              borderRadius="50%"
            />
          ) : (
            <Typography fontSize={36} color="primary">
              {initials}
            </Typography>
          )}
        </Stack>
        <Stack direction="column" gap={1}>
          <Slider
            value={state.scale}
            onChange={handleSliderChange}
            min={ZOOM_PAN_IMAGE_MIN_SCALE}
            max={ZOOM_PAN_IMAGE_MAX_SCALE}
            step={0.1}
            sx={{ width: 300 }}
          />
          <Typography variant="caption" color="text.secondary">
            {t('files.adjustYourImageToFit')}
          </Typography>
        </Stack>
        <Stack spacing={2} gap={2} flexDirection="row" alignItems="center">
          <FileUploadButton
            maxFileSize={DEFAULT_MAX_IMAGE_SIZE_IN_MB}
            startIcon={<CameraIcon />}
            onClick={handleFileChange}
            onError={(message) => setState((prevState) => ({ ...prevState, errorMessage: message }))}
          >
            {t('files.replaceImage')}
          </FileUploadButton>
          <Button
            variant="contained"
            sx={{ margin: '0 !important' }}
            disabled={!state.imageSrc}
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleFileRemove}
          >
            {t('files.removeImage')}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};
